import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Target, Users, Lightbulb, Building2 } from "lucide-react";

// EV charging with city skyline - clean mobility
const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_green-tech-hyd/artifacts/fhvx6n98_WhatsApp%20Image%202026-04-06%20at%2011.46.47.jpeg";

const highlights = [
  {
    icon: Target,
    title: "Mission-Driven",
    description: "Focused on sustainable mobility technology development",
  },
  {
    icon: Users,
    title: "Global Network",
    description: "Connecting industry leaders, startups, and governments",
  },
  {
    icon: Lightbulb,
    title: "Innovation Hub",
    description: "Platform for R&D, testing, and market incubation",
  },
  {
    icon: Building2,
    title: "Industry Verticals",
    description: "OEMs, EVs, Hydrogen, Solar, and Cleantech",
  },
];

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src={ABOUT_IMAGE}
                alt="Modern sustainable architecture"
                className="w-full h-full object-cover img-zoom"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent" />
            </div>
            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="absolute -bottom-6 -right-6 bg-emerald-700 text-white px-6 py-4 rounded-xl shadow-lg"
            >
              <div className="text-3xl font-bold">T4EM</div>
              <div className="text-sm text-emerald-100">Non-Profit Foundation</div>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-4 block">
              About Us
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mb-6 tracking-tight">
              A Platform for Sustainable Mobility Innovation
            </h2>
            <div className="space-y-4 text-stone-600 leading-relaxed mb-8">
              <p>
                Tech4Earth Mobility (T4EM) is a mobility-focused non-profit
                organisation dedicated to the development and adaptation of
                sustainable Mobility technologies worldwide.
              </p>
              <p>
                We provide a platform that brings together technology leaders,
                Government and Industry stakeholders, International innovators,
                Startups, and industry-leading entrepreneurs under one umbrella
                with the singular objective of advancing sustainable and
                earth-friendly Mobility technologies.
              </p>
              <p>
                Our members encompass industry verticals from Automotive OEMs, EV
                manufacturing, Hydrogen, Electronics, renewable energy, Solar,
                research institutes, universities, and cleantech startups.
              </p>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-2 gap-4">
              {highlights.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-stone-50 border border-stone-100"
                >
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <item.icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <div className="font-medium text-stone-900 text-sm">
                      {item.title}
                    </div>
                    <div className="text-xs text-stone-500">{item.description}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
